import { useState, useCallback, useEffect, useRef } from 'react'
import Board from '@cloudscape-design/board-components/board'
import BoardItem from '@cloudscape-design/board-components/board-item'
import type { BoardProps } from '@cloudscape-design/board-components/board'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Container from '@cloudscape-design/components/container'
import DateRangePicker from '@cloudscape-design/components/date-range-picker'
import type { DateRangePickerProps } from '@cloudscape-design/components/date-range-picker'
import Header from '@cloudscape-design/components/header'
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs'
import Select from '@cloudscape-design/components/select'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Page from '@/components/common/Page'
import { formatDate } from '@/utils/format'
import { useClientsNav } from '@/hooks/useClients'
import { useDashboardStats } from '@/hooks/useDashboardStats'
import { useGbpLocations } from '@/hooks/useGbpLocations'
import { useGbpReviews } from '@/hooks/useGbpReviews'
import { useSiteVisits } from '@/hooks/useSiteVisits'
import ContentOverviewWidget from '@/pages/dashboard/widgets/ContentOverviewWidget'
import EmailOutreachWidget from '@/pages/dashboard/widgets/EmailOutreachWidget'
import GbpReviewsWidget from '@/pages/dashboard/widgets/GbpReviewsWidget'
import KeywordRankingsWidget from '@/pages/dashboard/widgets/KeywordRankingsWidget'
import SiteVisitsWidget from '@/pages/dashboard/widgets/SiteVisitsWidget'
import { useEmailResponseStats } from '@/hooks/useEmailResponseStats'
import { usePostPerformance } from '@/hooks/usePostPerformance'
import EmailResponsesWidget from '@/pages/dashboard/widgets/EmailResponsesWidget'
import SocialPerformanceWidget from '@/pages/dashboard/widgets/SocialPerformanceWidget'
import { useDashboardInsights } from '../../hooks/useDashboardInsights'
import AIInsightsSummaryWidget from './widgets/AIInsightsSummaryWidget'

export function resolveDateRange(range: DateRangePickerProps.Value | null): { startDate?: string; endDate?: string } {
  if (!range) return {}
  if (range.type === 'absolute') return { startDate: range.startDate, endDate: range.endDate }
  const end = new Date()
  const start = new Date()
  if (range.unit === 'day') {
    start.setDate(start.getDate() - range.amount)
  } else if (range.unit === 'week') {
    start.setDate(start.getDate() - range.amount * 7)
  } else if (range.unit === 'month') {
    start.setMonth(start.getMonth() - range.amount)
  } else {
    start.setFullYear(start.getFullYear() - range.amount)
  }
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  }
}

type WidgetData = { title: string }
type DashboardItem = BoardProps.Item<WidgetData>

const WIDGET_DESCRIPTIONS: Record<string, string> = {
  'site-visits':      'Daily website sessions from Google Analytics 4. Reflects the selected date range.',
  'gbp-reviews':      'Star rating distribution and recent reviews from Google Business Profile. Click a row to read individual reviews.',
  'keyword-rankings': 'Search position, clicks, impressions, and CTR from Google Search Console. Enter comma-separated keywords to filter.',
  'email-outreach':   'Outreach emails generated by Sabar. Counts link to the Queue filtered by status.',
  'content-overview': 'All content pieces generated by Sabar across campaigns. Counts link to the Queue filtered by status.',
  'email-responses':        'Emails sent and replies over time. Reply tracking requires Gmail to be connected in Settings.',
  'social-performance':     'Posts published and engagement (likes, comments, shares) per connected social platform.',
}

function formatDateRangeLabel(range: DateRangePickerProps.Value | null): string {
  if (!range) return 'all time'
  if (range.type === 'relative') {
    return `last ${range.amount} ${range.unit}${range.amount !== 1 ? 's' : ''}`
  }
  return `${formatDate(range.startDate)} – ${formatDate(range.endDate)}`
}

const SELECTED_CLIENT_KEY = 'sabar-dashboard-client-v1'
const LAYOUT_KEY_PREFIX = 'sabar-dashboard-layout-v1'

const DEFAULT_ITEMS: DashboardItem[] = [
  { id: 'site-visits',      rowSpan: 4, columnSpan: 4, data: { title: 'Site Visits' },      columnOffset: { 4: 0 } },
  { id: 'gbp-reviews',      rowSpan: 4, columnSpan: 2, data: { title: 'GBP Reviews' },      columnOffset: { 4: 0 } },
  { id: 'keyword-rankings', rowSpan: 4, columnSpan: 2, data: { title: 'Keyword Rankings' }, columnOffset: { 4: 2 } },
  { id: 'email-outreach',   rowSpan: 4, columnSpan: 2, data: { title: 'Email Outreach' },   columnOffset: { 4: 0 } },
  { id: 'content-overview', rowSpan: 4, columnSpan: 2, data: { title: 'Content Overview' }, columnOffset: { 4: 2 } },
  { id: 'email-responses',    rowSpan: 5, columnSpan: 4, data: { title: 'Email Responses' },    columnOffset: { 4: 0 } },
  { id: 'social-performance', rowSpan: 5, columnSpan: 4, data: { title: 'Social Performance' }, columnOffset: { 4: 0 } },
]

function loadLayout(clientId: string): DashboardItem[] {
  try {
    const saved = localStorage.getItem(`${LAYOUT_KEY_PREFIX}-${clientId}`)
    if (!saved) return DEFAULT_ITEMS
    const parsed = JSON.parse(saved) as DashboardItem[]
    const knownIds = new Set(DEFAULT_ITEMS.map(i => i.id))
    const valid = parsed.filter(i => knownIds.has(i.id))
    const savedIds = new Set(valid.map(i => i.id))
    const missing = DEFAULT_ITEMS.filter(i => !savedIds.has(i.id))
    return [...valid, ...missing]
  } catch {
    return DEFAULT_ITEMS
  }
}

function saveLayout(clientId: string, items: readonly DashboardItem[]): void {
  localStorage.setItem(`${LAYOUT_KEY_PREFIX}-${clientId}`, JSON.stringify(items))
}

const BOARD_I18N: BoardProps.I18nStrings<WidgetData> = {
  liveAnnouncementDndStarted: (op) => `Picked up item. ${op} operation.`,
  liveAnnouncementDndDiscarded: () => 'Move cancelled.',
  liveAnnouncementDndCommitted: (op) => `${op} committed.`,
  liveAnnouncementDndItemReordered: ({ item, placement }) =>
    `Item ${item.data.title} moved to row ${placement.y} col ${placement.x}.`,
  liveAnnouncementDndItemResized: ({ item, placement }) =>
    `Item ${item.data.title} resized to ${placement.height}×${placement.width}.`,
  liveAnnouncementDndItemInserted: ({ item, placement }) =>
    `Item ${item.data.title} inserted at row ${placement.y} col ${placement.x}.`,
  liveAnnouncementItemRemoved: ({ item }) => `Item ${item.data.title} removed.`,
  navigationAriaLabel: 'Dashboard widgets',
  navigationAriaDescription: 'Navigate between widgets.',
  navigationItemAriaLabel: (item) => item?.data.title ?? 'Empty slot',
}

const BOARD_ITEM_I18N = {
  dragHandleAriaLabel: 'Drag handle',
  dragHandleAriaDescription: 'Use Space or Enter to activate drag, arrow keys to move, Space or Enter to confirm, Escape to cancel.',
  resizeHandleAriaLabel: 'Resize handle',
  resizeHandleAriaDescription: 'Use Space or Enter to activate resize, arrow keys to adjust, Space or Enter to confirm, Escape to cancel.',
}

export default function DashboardPage() {
  const { clients } = useClientsNav()

  const [selectedClientId, setSelectedClientId] = useState<string | null>(() =>
    localStorage.getItem(SELECTED_CLIENT_KEY)
  )
  const effectiveClientId = selectedClientId ?? clients[0]?.id ?? null

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null)

  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(() => {
    try {
      const saved = localStorage.getItem('sabar-dashboard-daterange-v1')
      return saved ? JSON.parse(saved) : null
    } catch {
      return null
    }
  })

  const [boardItems, setBoardItems] = useState<DashboardItem[]>(() => {
    const cid = localStorage.getItem(SELECTED_CLIENT_KEY)
    return cid ? loadLayout(cid) : DEFAULT_ITEMS
  })

  // When no explicit client was selected, effectiveClientId resolves asynchronously
  // from clients[0]?.id after the API loads. Load the saved layout for that client once.
  const layoutLoadedForRef = useRef<string | null>(localStorage.getItem(SELECTED_CLIENT_KEY))
  useEffect(() => {
    if (selectedClientId !== null) return
    if (!effectiveClientId) return
    if (layoutLoadedForRef.current === effectiveClientId) return
    layoutLoadedForRef.current = effectiveClientId
    setBoardItems(loadLayout(effectiveClientId))
  }, [effectiveClientId, selectedClientId])

  const { startDate, endDate } = resolveDateRange(dateRange)
  const dateRangeLabel = formatDateRangeLabel(dateRange)

  const siteUrl = clients.find(c => c.id === effectiveClientId)?.websiteUrl ?? undefined
  const { stats, loading: statsLoading } = useDashboardStats(effectiveClientId, { startDate, endDate })
  const { locations, loading: locationsLoading } = useGbpLocations(effectiveClientId)
  const { reviews, loading: reviewsLoading } = useGbpReviews(effectiveClientId, selectedLocationId)
  const { summary, loading: visitsLoading } = useSiteVisits(effectiveClientId, selectedLocationId, { startDate, endDate })
  const { stats: emailResponseStats, loading: emailResponseLoading } = useEmailResponseStats(effectiveClientId, { startDate, endDate })
  const { stats: postPerfStats, loading: postPerfLoading } = usePostPerformance(effectiveClientId, { startDate, endDate })
  const { insights, loading: insightsLoading, refreshing, refresh } = useDashboardInsights(effectiveClientId)

  const handleClientChange = useCallback(({ detail }: { detail: { selectedOption: { value?: string } } }) => {
    const id = detail.selectedOption.value ?? null
    if (!id) return
    setSelectedClientId(id)
    localStorage.setItem(SELECTED_CLIENT_KEY, id)
    setBoardItems(loadLayout(id))
    setSelectedLocationId(null)
  }, [])

  const handleLocationChange = useCallback(({ detail }: { detail: { selectedOption: { value?: string } } }) => {
    const id = detail.selectedOption.value
    setSelectedLocationId(id === '__all__' ? null : (id ?? null))
  }, [])

  const handleItemsChange = useCallback(({ detail }: { detail: { items: readonly DashboardItem[] } }) => {
    setBoardItems([...detail.items])
    if (effectiveClientId) saveLayout(effectiveClientId, detail.items)
  }, [effectiveClientId])

  const handleResetLayout = useCallback(() => {
    setBoardItems(DEFAULT_ITEMS)
    if (effectiveClientId) {
      localStorage.removeItem(`${LAYOUT_KEY_PREFIX}-${effectiveClientId}`)
    }
  }, [effectiveClientId])

  const handleDateRangeChange = useCallback((value: DateRangePickerProps.Value | null) => {
    setDateRange(value)
    localStorage.setItem('sabar-dashboard-daterange-v1', JSON.stringify(value))
  }, [])

  const handleRemoveWidget = useCallback((id: string) => {
    setBoardItems(items => items.filter(item => item.id !== id))
    if (effectiveClientId) {
      saveLayout(effectiveClientId, boardItems.filter(item => item.id !== id))
    }
  }, [effectiveClientId, boardItems])

  const clientOptions = clients.map(c => ({ label: c.agentName ?? c.brandName, value: c.id }))
  const selectedOption = clientOptions.find(o => o.value === effectiveClientId) ?? null

  const locationOptions = [
    { label: 'All locations', value: '__all__' },
    ...locations.map(l => ({ label: l.locationName, value: l.locationId })),
  ]
  const selectedLocationOption = locationOptions.find(o => o.value === (selectedLocationId ?? '__all__')) ?? locationOptions[0]

  function renderWidgetContent(id: string) {
    switch (id) {
      case 'gbp-reviews':
        return <GbpReviewsWidget reviews={reviews} loading={reviewsLoading} clientId={effectiveClientId} insights={insights?.perWidget.gbpReviews} />
      case 'keyword-rankings':
        return (
          <KeywordRankingsWidget
            clientId={effectiveClientId}
            locationId={selectedLocationId}
            startDate={startDate}
            endDate={endDate}
            insights={insights?.perWidget.keywordRankings}
          />
        )
      case 'site-visits':
        return <SiteVisitsWidget summary={summary} loading={visitsLoading} dateRangeLabel={dateRangeLabel} siteUrl={siteUrl} insights={insights?.perWidget.siteVisits} />
      case 'content-overview':
        return <ContentOverviewWidget stats={stats} loading={statsLoading} clientId={effectiveClientId} insights={insights?.perWidget.contentOverview} />
      case 'email-outreach':
        return <EmailOutreachWidget stats={stats?.emailOutreach ?? null} loading={statsLoading} clientId={effectiveClientId} insights={insights?.perWidget.emailOutreach} />
      case 'email-responses':
        return <EmailResponsesWidget stats={emailResponseStats} loading={emailResponseLoading} insights={insights?.perWidget.emailResponses} />
      case 'social-performance':
        return <SocialPerformanceWidget contentStats={stats} performanceStats={postPerfStats} loading={statsLoading || postPerfLoading} insights={insights?.perWidget.socialPerformance} />
      default:
        return null
    }
  }

  return (
    <Page
      title="Dashboard"
      description="Analytics across all connected channels"
      actions={<Button onClick={handleResetLayout}>Reset layout</Button>}
      helpContent={{
        header: 'Dashboard',
        body: (
          <Box>
            <p>The Dashboard provides a real-time overview of your marketing performance across all connected channels and platforms.</p>
            <h4>Key Features:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li><strong>Site Visits</strong> - Daily website traffic from Google Analytics 4</li>
              <li><strong>GBP Reviews</strong> - Google Business Profile ratings and reviews</li>
              <li><strong>Keyword Rankings</strong> - Search position from Google Search Console</li>
              <li><strong>Email Outreach</strong> - Count of emails generated by Sabar</li>
              <li><strong>Content Overview</strong> - Total content pieces generated</li>
              <li><strong>Email Responses</strong> - Email send volume and reply tracking</li>
              <li><strong>Social Performance</strong> - Posts published and engagement per platform</li>
            </ul>
            <h4>How to Use:</h4>
            <p>Select a client from the dropdown. Use the date range picker to filter data. Drag and drop widgets to customize your layout.</p>
          </Box>
        ),
      }}
    >
      <SpaceBetween size="l">
      <SpaceBetween direction="horizontal" size="s">
        <Select
          selectedOption={selectedOption}
          onChange={handleClientChange}
          options={clientOptions}
          placeholder="Select a client"
          empty="No clients found"
          ariaLabel="Select client"
        />
        <Select
          selectedOption={selectedLocationOption}
          onChange={handleLocationChange}
          options={locationOptions}
          disabled={!effectiveClientId || locationsLoading}
          statusType={locationsLoading ? 'loading' : 'finished'}
          loadingText="Loading locations…"
          empty="No locations found"
          ariaLabel="Select GBP location"
        />
      </SpaceBetween>

      <Container
        header={<Header variant="h2">Summary</Header>}
      >
        <ColumnLayout columns={4}>
          <KeyValuePairs
            columns={1}
            items={[
              { label: 'Generated', value: stats?.totalGenerated ?? 0 },
            ]}
          />
          <KeyValuePairs
            columns={1}
            items={[
              { label: 'Pending Review', value: stats?.totalPendingReview ?? 0 },
            ]}
          />
          <KeyValuePairs
            columns={1}
            items={[
              { label: 'Emails Sent', value: emailResponseStats?.totalSent ?? 0 },
            ]}
          />
          <KeyValuePairs
            columns={1}
            items={[
              { label: 'Reply Rate', value: `${(emailResponseStats?.replyRate ?? 0).toFixed(1)}%` },
            ]}
          />
        </ColumnLayout>
      </Container>

      <DateRangePicker
        value={dateRange}
        onChange={({ detail }) => handleDateRangeChange(detail.value)}
        relativeOptions={[
          { key: 'last-7-days',  amount: 7,  unit: 'day',  type: 'relative' },
          { key: 'last-30-days', amount: 30, unit: 'day',  type: 'relative' },
          { key: 'last-90-days', amount: 90, unit: 'day',  type: 'relative' },
        ]}
        isValidRange={(range) => {
          if (!range) return { valid: true }
          if (range.type === 'absolute' && (!range.startDate || !range.endDate)) {
            return { valid: false, errorMessage: 'Select both start and end dates.' }
          }
          return { valid: true }
        }}
        placeholder="Filter by date range"
        i18nStrings={{
          todayAriaLabel: 'Today',
          nextMonthAriaLabel: 'Next month',
          previousMonthAriaLabel: 'Previous month',
          customRelativeRangeDurationLabel: 'Duration',
          customRelativeRangeDurationPlaceholder: 'Enter duration',
          customRelativeRangeOptionLabel: 'Custom range',
          customRelativeRangeOptionDescription: 'Set a custom range in the past',
          customRelativeRangeUnitLabel: 'Unit of time',
          formatRelativeRange: (e) => `Last ${e.amount} ${e.unit}${e.amount > 1 ? 's' : ''}`,
          formatUnit: (e, n) => (n === 1 ? e : `${e}s`),
          dateTimeConstraintText: '',
          relativeModeTitle: 'Relative range',
          absoluteModeTitle: 'Absolute range',
          relativeRangeSelectionHeading: 'Choose a range',
          startDateLabel: 'Start date',
          startTimeLabel: 'Start time',
          endDateLabel: 'End date',
          endTimeLabel: 'End time',
          clearButtonLabel: 'Clear',
          cancelButtonLabel: 'Cancel',
          applyButtonLabel: 'Apply',
        }}
      />

      <AIInsightsSummaryWidget
        insights={insights}
        loading={insightsLoading}
        refreshing={refreshing}
        onRefresh={refresh}
      />

      <Board<WidgetData>
        items={boardItems}
        renderItem={(item) => (
          <BoardItem
            header={<Header description={WIDGET_DESCRIPTIONS[item.id]}>{item.data.title}</Header>}
            i18nStrings={BOARD_ITEM_I18N}
            settings={
              <Button
                iconName="close"
                variant="icon"
                ariaLabel={`Remove ${item.data.title} widget`}
                onClick={() => handleRemoveWidget(item.id)}
              />
            }
          >
            {renderWidgetContent(item.id)}
          </BoardItem>
        )}
        onItemsChange={handleItemsChange}
        i18nStrings={BOARD_I18N}
        empty={
          <Box textAlign="center" padding="l" color="text-body-secondary">
            No widgets
          </Box>
        }
      />
      </SpaceBetween>
    </Page>
  )
}
